import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  applyFeatureFlagOperations,
  computeFeatureFlagDiff,
  type FeatureFlagDiff,
  type FeatureFlagOp,
} from '@shared';
import {
  appConfigFeatureFlagsApi,
  appConfigFeatureFlagsKeys,
  type FeatureFlagDraft,
} from '../api/api';
import { reconcileOperation } from '../utils/reconcile-operations';

function toastApiError(prefix: string, error: unknown) {
  const message = error instanceof Error ? error.message : 'Unknown error';
  toast.error(`${prefix}: ${message}`);
}

type UseFeatureFlagDraftArgs = {
  original: AppConfigSchema | undefined;
  basedOnHash: string | undefined;
};

export function useFeatureFlagDraft({ original, basedOnHash }: UseFeatureFlagDraftArgs) {
  const queryClient = useQueryClient();

  const draftQuery = useQuery({
    queryKey: appConfigFeatureFlagsKeys.draft(),
    queryFn: () => appConfigFeatureFlagsApi.fetchDraft(),
    staleTime: 30_000,
  });

  const draft: FeatureFlagDraft | null = draftQuery.data?.draft ?? null;
  const operations: FeatureFlagOp[] = draft?.operations ?? [];

  const putMutation = useMutation({
    mutationFn: appConfigFeatureFlagsApi.putDraft,
    onSuccess: ({ draft: saved }) => {
      queryClient.setQueryData(appConfigFeatureFlagsKeys.draft(), { draft: saved });
    },
    onError: error => toastApiError('Could not save draft', error),
  });

  const deleteMutation = useMutation({
    mutationFn: appConfigFeatureFlagsApi.deleteDraft,
    onSuccess: () => {
      queryClient.setQueryData(appConfigFeatureFlagsKeys.draft(), { draft: null });
    },
    onError: error => toastApiError('Could not clear draft', error),
  });

  const commitOps = useCallback(
    (nextOps: FeatureFlagOp[]) => {
      if (!basedOnHash) return;
      if (nextOps.length === 0) {
        if (draft) deleteMutation.mutate();
        return;
      }
      putMutation.mutate({ basedOnHash, operations: nextOps });
    },
    [basedOnHash, draft, deleteMutation, putMutation],
  );

  const enqueueOp = useCallback(
    (op: FeatureFlagOp) => {
      if (!original) return;
      commitOps(reconcileOperation(original, operations, op));
    },
    [original, operations, commitOps],
  );

  const removeOpAtIndex = useCallback(
    (index: number) => {
      commitOps(operations.filter((_, i) => i !== index));
    },
    [operations, commitOps],
  );

  const clearAll = useCallback(() => {
    if (draft) deleteMutation.mutate();
  }, [draft, deleteMutation]);

  const targetConfig = useMemo<AppConfigSchema | undefined>(
    () => (original ? applyFeatureFlagOperations(original, operations) : undefined),
    [original, operations],
  );

  const diff = useMemo<FeatureFlagDiff>(() => {
    if (!original || !targetConfig) return { entries: [], hasChanges: false };
    return computeFeatureFlagDiff(original, targetConfig);
  }, [original, targetConfig]);

  const isBehindLatest = Boolean(
    draft && basedOnHash && draft.basedOnHash !== basedOnHash,
  );

  return {
    draft,
    operations,
    targetConfig,
    diff,
    isPending: draftQuery.isPending,
    isSaving: putMutation.isPending || deleteMutation.isPending,
    isBehindLatest,
    enqueueOp,
    removeOpAtIndex,
    clearAll,
    refetch: draftQuery.refetch,
  };
}
